/**
 * ⚡ Fissal Prime Dynamic Connection Manager
 * Intelligently routes between:
 * - Primary Home LAN: http://192.168.68.50:8092 (fast timeout <250ms)
 * - Fallback Sovereign Tailscale: http://100.83.56.28:8092
 * - Fallback Public Relay: https://gateway.ech-o.net (SSL edge for family/cellular)
 * 
 * Provides transparent zero-drop failover during Wi-Fi <-> 5G / cellular transitions.
 */

export type NetworkRoute = 'LAN' | 'TAILSCALE' | 'PUBLIC' | 'CUSTOM';

export interface RouteStatus {
  route: NetworkRoute;
  url: string;
  latencyMs: number;
  isOnline: boolean;
  lastChecked: number;
}

export const PRIMARY_LAN_URL = 'http://192.168.68.50:8092';
export const FALLBACK_TAILSCALE_URL = 'http://100.83.56.28:8092';
export const PUBLIC_RELAY_URL = 'https://gateway.ech-o.net';
export const FAST_LAN_TIMEOUT_MS = 850; // Mesh / AP tolerance
export const TAILSCALE_TIMEOUT_MS = 3500;
export const PUBLIC_TIMEOUT_MS = 4000;

const STORAGE_KEY_SERVER_URL = 'fissal_server_url';
const STORAGE_KEY_NETWORK_PRESET = 'fissal_network_preset'; // 'AUTO' | 'LAN' | 'TAILSCALE' | 'PUBLIC' | 'CUSTOM'

type RouteChangeListener = (status: RouteStatus) => void;

class ConnectionManager {
  private currentUrl: string;
  private currentRoute: NetworkRoute;
  private latencyMs: number = 0;
  private isOnline: boolean = true;
  private isProbing: boolean = false;
  private lastChecked: number = 0;
  private listeners: Set<RouteChangeListener> = new Set();
  private heartbeatTimer: any = null;
  private presetMode: 'AUTO' | 'LAN' | 'TAILSCALE' | 'PUBLIC' | 'CUSTOM' = 'AUTO';

  // Symmetric Hysteresis & Anti-Flap Guard
  private consecutiveLanFailures: number = 0;
  private consecutiveLanSuccesses: number = 0;
  private lastRouteSwitchTime: number = 0;
  private readonly HYSTERESIS_THRESHOLD = 3;
  private readonly ROUTE_SWITCH_COOLDOWN_MS = 20000;

  constructor() {
    const savedPreset = localStorage.getItem(STORAGE_KEY_NETWORK_PRESET) as any;
    if (savedPreset) {
      this.presetMode = savedPreset;
    }

    const savedUrl = localStorage.getItem(STORAGE_KEY_SERVER_URL);
    if (this.presetMode === 'LAN') {
      this.currentUrl = PRIMARY_LAN_URL;
      this.currentRoute = 'LAN';
    } else if (this.presetMode === 'TAILSCALE') {
      this.currentUrl = FALLBACK_TAILSCALE_URL;
      this.currentRoute = 'TAILSCALE';
    } else if (this.presetMode === 'PUBLIC') {
      this.currentUrl = PUBLIC_RELAY_URL;
      this.currentRoute = 'PUBLIC';
    } else if (savedUrl && !savedUrl.includes('192.168.68.50') && !savedUrl.includes('100.83.56.28') && !savedUrl.includes('gateway.ech-o.net')) {
      this.currentUrl = savedUrl;
      this.currentRoute = 'CUSTOM';
      this.presetMode = 'CUSTOM';
    } else {
      // Default auto: start with LAN or saved
      this.currentUrl = savedUrl || PRIMARY_LAN_URL;
      this.currentRoute = this.currentUrl.includes('192.168.68.50')
        ? 'LAN'
        : this.currentUrl.includes('100.83.56.28')
        ? 'TAILSCALE'
        : this.currentUrl.includes('gateway.ech-o.net')
        ? 'PUBLIC'
        : 'LAN';
      this.presetMode = 'AUTO';
    }

    this.initEventListeners();
    this.startHeartbeat();
    setTimeout(() => this.resolveFastestRoute(), 50);
  }

  private initEventListeners() {
    if (typeof window === 'undefined') return;

    window.addEventListener('online', () => {
      console.log('🐾 [ConnectionManager] Browser reported online — probing fastest link...');
      this.resolveFastestRoute();
    });

    window.addEventListener('focus', () => {
      if (Date.now() - this.lastChecked > 5000) {
        this.resolveFastestRoute();
      }
    });

    const nav = navigator as any;
    if (nav.connection && typeof nav.connection.addEventListener === 'function') {
      nav.connection.addEventListener('change', () => {
        console.log('🐾 [ConnectionManager] Network interface changed — resolving route...');
        this.resolveFastestRoute();
      });
    }
  }

  private startHeartbeat() {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.resolveFastestRoute();
      }
    }, 10000);
  }

  public getActiveUrl(): string {
    return this.currentUrl;
  }

  public getActiveRoute(): NetworkRoute {
    return this.currentRoute;
  }

  public getLatency(): number {
    return this.latencyMs;
  }

  public getStatus(): RouteStatus {
    return {
      route: this.currentRoute,
      url: this.currentUrl,
      latencyMs: this.latencyMs,
      isOnline: this.isOnline,
      lastChecked: this.lastChecked
    };
  }

  public getPresetMode(): 'AUTO' | 'LAN' | 'TAILSCALE' | 'PUBLIC' | 'CUSTOM' {
    return this.presetMode;
  }

  public setPresetMode(mode: 'AUTO' | 'LAN' | 'TAILSCALE' | 'PUBLIC' | 'CUSTOM', customUrl?: string) {
    this.presetMode = mode;
    localStorage.setItem(STORAGE_KEY_NETWORK_PRESET, mode);

    if (mode === 'LAN') {
      this.setUrl(PRIMARY_LAN_URL, 'LAN');
    } else if (mode === 'TAILSCALE') {
      this.setUrl(FALLBACK_TAILSCALE_URL, 'TAILSCALE');
    } else if (mode === 'PUBLIC') {
      this.setUrl(PUBLIC_RELAY_URL, 'PUBLIC');
    } else if (mode === 'CUSTOM' && customUrl) {
      this.setUrl(customUrl.replace(/\/+$/, ''), 'CUSTOM');
    } else {
      this.resolveFastestRoute();
    }
  }

  public getWsUrl(): string {
    const httpUrl = this.currentUrl;
    const wsBase = httpUrl.replace(/^http:\/\//i, 'ws://').replace(/^https:\/\//i, 'wss://');
    return `${wsBase}/ws`;
  }

  private setUrl(url: string, route: NetworkRoute, latency: number = 0, online: boolean = true) {
    const cleanUrl = url.replace(/\/+$/, '');
    const hasChanged = this.currentUrl !== cleanUrl || this.currentRoute !== route || this.isOnline !== online;

    this.currentUrl = cleanUrl;
    this.currentRoute = route;
    this.latencyMs = latency;
    this.isOnline = online;
    this.lastChecked = Date.now();

    localStorage.setItem(STORAGE_KEY_SERVER_URL, cleanUrl);

    if (hasChanged) {
      console.log(`🐾 [ConnectionManager] Active link switched to ${route}: ${cleanUrl} (${latency}ms)`);
      this.notifyListeners();
    }
  }

  public subscribe(listener: RouteChangeListener): () => void {
    this.listeners.add(listener);
    listener(this.getStatus());
    return () => this.listeners.delete(listener);
  }

  private notifyListeners() {
    const status = this.getStatus();
    this.listeners.forEach(fn => {
      try {
        fn(status);
      } catch (err) {
        console.error('[ConnectionManager] Listener error:', err);
      }
    });
  }

  public async probeEndpoint(targetUrl: string, timeoutMs: number): Promise<{ ok: boolean; latencyMs: number }> {
    const start = performance.now();
    try {
      const res = await fetch(`${targetUrl.replace(/\/+$/, '')}/healthz`, {
        method: 'GET',
        signal: AbortSignal.timeout(timeoutMs),
        headers: { Accept: 'application/json' }
      });
      const latency = Math.max(1, Math.round(performance.now() - start));
      return { ok: res.ok, latencyMs: latency };
    } catch {
      return { ok: false, latencyMs: -1 };
    }
  }

  public async resolveFastestRoute(): Promise<RouteStatus> {
    if (this.presetMode !== 'AUTO') {
      const check = await this.probeEndpoint(this.currentUrl, TAILSCALE_TIMEOUT_MS);
      this.latencyMs = check.latencyMs > 0 ? check.latencyMs : this.latencyMs;
      const onlineChanged = this.isOnline !== check.ok;
      this.isOnline = check.ok;
      this.lastChecked = Date.now();
      if (onlineChanged) {
        this.notifyListeners();
      }
      return this.getStatus();
    }

    if (this.isProbing) return this.getStatus();
    this.isProbing = true;

    try {
      const now = Date.now();
      const inCooldown = (now - this.lastRouteSwitchTime) < this.ROUTE_SWITCH_COOLDOWN_MS;

      // 1. Probe Primary LAN
      const lanCheck = await this.probeEndpoint(PRIMARY_LAN_URL, FAST_LAN_TIMEOUT_MS);

      if (lanCheck.ok) {
        this.consecutiveLanSuccesses++;
        this.consecutiveLanFailures = 0;

        if (this.currentRoute === 'LAN') {
          this.setUrl(PRIMARY_LAN_URL, 'LAN', lanCheck.latencyMs, true);
          return this.getStatus();
        }

        if (this.consecutiveLanSuccesses >= this.HYSTERESIS_THRESHOLD && !inCooldown) {
          console.log(`🐾 [ConnectionManager] LAN confirmed stable (${this.consecutiveLanSuccesses} successes). Switching to LAN.`);
          this.lastRouteSwitchTime = now;
          this.setUrl(PRIMARY_LAN_URL, 'LAN', lanCheck.latencyMs, true);
          return this.getStatus();
        }

        this.isOnline = true;
        this.lastChecked = now;
        return this.getStatus();
      } else {
        // LAN probe failed
        this.consecutiveLanFailures++;
        this.consecutiveLanSuccesses = 0;

        if (this.currentRoute === 'LAN') {
          if (this.consecutiveLanFailures < this.HYSTERESIS_THRESHOLD) {
            console.log(`🐾 [ConnectionManager] LAN probe missed (${this.consecutiveLanFailures}/${this.HYSTERESIS_THRESHOLD}), holding LAN route.`);
            return this.getStatus();
          }
        }

        // LAN unreachable: probe Tailscale first, then Public Relay
        const tailscaleCheck = await this.probeEndpoint(FALLBACK_TAILSCALE_URL, TAILSCALE_TIMEOUT_MS);
        if (tailscaleCheck.ok) {
          this.lastRouteSwitchTime = now;
          this.setUrl(FALLBACK_TAILSCALE_URL, 'TAILSCALE', tailscaleCheck.latencyMs, true);
          return this.getStatus();
        }

        // Tailscale unreachable: probe Public Relay (gateway.ech-o.net)
        const publicCheck = await this.probeEndpoint(PUBLIC_RELAY_URL, PUBLIC_TIMEOUT_MS);
        if (publicCheck.ok) {
          this.lastRouteSwitchTime = now;
          this.setUrl(PUBLIC_RELAY_URL, 'PUBLIC', publicCheck.latencyMs, true);
          return this.getStatus();
        }

        this.isOnline = false;
        this.lastChecked = now;
        this.notifyListeners();
        return this.getStatus();
      }
    } finally {
      this.isProbing = false;
    }
  }

  public async fetchWithFailover(path: string, init?: RequestInit): Promise<Response> {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const isAiChat = cleanPath.includes('/api/fissal/chat');
    const isMediaUpload = cleanPath.includes('/upload');
    const baseTimeoutMs = isAiChat ? 65000 : (isMediaUpload ? 45000 : (this.currentRoute === 'LAN' ? 6000 : 12000));
    const primaryTimeoutMs = baseTimeoutMs;
    const primaryTarget = `${this.currentUrl}${cleanPath}`;

    const executeAttempt = async (targetUrl: string, waitMs: number): Promise<Response> => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(new Error(`Timeout after ${waitMs}ms`)), waitMs);

      if (init?.signal) {
        if (init.signal.aborted) {
          throw init.signal.reason || new Error('Aborted by caller');
        }
        init.signal.addEventListener('abort', () => controller.abort(init.signal?.reason), { once: true });
      }

      try {
        const fetchInit = { ...init, signal: controller.signal };
        return await fetch(targetUrl, fetchInit);
      } finally {
        clearTimeout(timeoutId);
      }
    };

    try {
      const response = await executeAttempt(primaryTarget, primaryTimeoutMs);
      this.isOnline = true;
      return response;
    } catch (err: any) {
      if (this.presetMode === 'CUSTOM') {
        throw err;
      }

      console.warn(`🐾 [ConnectionManager] Primary route ${this.currentRoute} failed (${err.message}). Attempting auto-failover...`);

      // Determine fallback sequence
      const fallbackRoutes: { url: string; route: NetworkRoute }[] = [];
      if (this.currentRoute === 'LAN') {
        fallbackRoutes.push({ url: FALLBACK_TAILSCALE_URL, route: 'TAILSCALE' });
        fallbackRoutes.push({ url: PUBLIC_RELAY_URL, route: 'PUBLIC' });
      } else if (this.currentRoute === 'TAILSCALE') {
        fallbackRoutes.push({ url: PRIMARY_LAN_URL, route: 'LAN' });
        fallbackRoutes.push({ url: PUBLIC_RELAY_URL, route: 'PUBLIC' });
      } else {
        fallbackRoutes.push({ url: PRIMARY_LAN_URL, route: 'LAN' });
        fallbackRoutes.push({ url: FALLBACK_TAILSCALE_URL, route: 'TAILSCALE' });
      }

      for (const fallback of fallbackRoutes) {
        try {
          const fallbackTarget = `${fallback.url}${cleanPath}`;
          const fallbackTimeoutMs = isAiChat ? 65000 : (isMediaUpload ? 45000 : (fallback.route === 'LAN' ? 6000 : 12000));
          const fallbackRes = await executeAttempt(fallbackTarget, fallbackTimeoutMs);
          this.setUrl(fallback.url, fallback.route, 35, true);
          console.log(`🐾 [ConnectionManager] Successfully failed over to ${fallback.route}!`);
          return fallbackRes;
        } catch {
          // Continue to next fallback
        }
      }

      this.isOnline = false;
      this.notifyListeners();
      throw new Error(`Connection failed across LAN, Tailscale, and Public Relay: ${err.message}`);
    }
  }
}

export const connectionManager = new ConnectionManager();
